<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>Residential Customer AR</v-card-title>
      <v-card-subtitle>By status, rating, and unpaid balance</v-card-subtitle>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="3"
          >
            <v-select
              v-model="filters.account_status"
              :items="statusOptions"
              label="Account Status"
              multiple
              chips
              small-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <v-select
              v-model="filters.district_id"
              :items="districts"
              item-text="name"
              item-value="id"
              label="District"
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model.number="filters.min_unpaid"
              label="Min Unpaid (LAK)"
              type="number"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model.number="filters.max_unpaid"
              label="Max Unpaid (LAK)"
              type="number"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            class="d-flex justify-end"
          >
            <v-btn
              color="primary"
              :loading="loading"
              @click="fetch"
            >
              Apply
            </v-btn>
            <v-btn
              class="ml-2"
              color="success"
              :loading="exporting"
              @click="exportExcel"
            >
              Export
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row
      class="mb-4"
      dense
    >
      <v-col
        cols="12"
        sm="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">Total Customers</div>
            <div class="text-h5">{{ summary.total_customers || 0 }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">Total Unpaid (LAK)</div>
            <div class="text-h5">{{ formatNumber(summary.total_unpaid_lak || 0) }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">By Rating</div>
            <div
              v-for="r in ['green', 'yellow', 'red']"
              :key="r"
              class="text-body-2"
            >
              {{ ratingLabel(r) }}: {{ (summary.by_rating && summary.by_rating[r]) ? summary.by_rating[r].count : 0 }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">By Status</div>
            <div
              v-for="st in ['active', 'inactive', 'deleted']"
              :key="st"
              class="text-body-2"
            >
              {{ st }}: {{ (summary.by_status && summary.by_status[st]) ? summary.by_status[st].count : 0 }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="100"
        :footer-props="{ 'items-per-page-options': [50, 100, 200, 500] }"
        dense
      >
        <template #item.package_price="{ item }">{{ formatNumber(item.package_price) }}</template>
        <template #item.unpaid_balance="{ item }">{{ formatNumber(item.unpaid_balance) }}</template>
        <template #item.account_status="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.account_status)"
            dark
          >
            {{ item.account_status }}
          </v-chip>
        </template>
        <template #item.can_collect="{ item }">
          <v-chip
            x-small
            :color="collectColor(item.can_collect)"
            dark
          >
            {{ collectLabel(item.can_collect) }}
          </v-chip>
        </template>
        <template #item.customer_rating="{ item }">
          <v-chip
            x-small
            :color="ratingColor(item.customer_rating)"
            dark
          >
            {{ ratingLabel(item.customer_rating) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import numberFormat from '../../Helpers/formatNumber';

export default {
  name: 'ReportResidentialCustomerAr',
  title() {
    return `Vientiane Waste Co-Dev|Residential Customer AR`;
  },
  data() {
    return {
      filters: {
        account_status: [],
        district_id: null,
        min_unpaid: null,
        max_unpaid: null,
      },
      statusOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Deleted', value: 'deleted' },
      ],
      districts: [],
      summary: {},
      items: [],
      loading: false,
      exporting: false,
      headers: [
        { text: 'ID', value: 'display_id' },
        { text: 'Name', value: 'name' },
        { text: 'Phone', value: 'phone' },
        { text: 'District', value: 'district' },
        { text: 'Village', value: 'village' },
        { text: 'Package Price (LAK)', value: 'package_price', align: 'right' },
        { text: 'Status', value: 'account_status' },
        { text: 'Collection', value: 'can_collect', align: 'center' },
        { text: 'Unpaid (LAK)', value: 'unpaid_balance', align: 'right' },
        { text: 'Aging (days)', value: 'aging_days', align: 'right' },
        { text: 'Rating', value: 'customer_rating', align: 'center' },
        { text: 'Created', value: 'created' },
      ],
    };
  },
  mounted() {
    this.loadDistricts();
    this.fetch();
  },
  methods: {
    formatNumber(n) {
      return numberFormat(n || 0);
    },
    statusColor(s) {
      const map = {
        active: 'green',
        inactive: 'orange',
        deleted: 'red',
      };
      return map[s] || 'grey';
    },
    ratingColor(r) {
      const map = {
        green: 'green',
        yellow: 'amber darken-2',
        red: 'red',
      };
      return map[r] || 'grey';
    },
    ratingLabel(r) {
      if (!r) return '';
      return r.charAt(0).toUpperCase() + r.slice(1);
    },
    collectColor(v) {
      if (v == 1) return 'success';
      if (v == 0) return 'error';
      return 'info';
    },
    collectLabel(v) {
      if (v == 1) return 'ເກັບໄດ້';
      if (v == 0) return 'ເກັບບໍ່ໄດ້';
      return '';
    },
    buildParams() {
      const p = {};
      if (this.filters.account_status.length) p.account_status = this.filters.account_status;
      if (this.filters.district_id) p.district_id = this.filters.district_id;
      if (this.filters.min_unpaid !== null && this.filters.min_unpaid !== '') p.min_unpaid = this.filters.min_unpaid;
      if (this.filters.max_unpaid !== null && this.filters.max_unpaid !== '') p.max_unpaid = this.filters.max_unpaid;
      return p;
    },
    async loadDistricts() {
      try {
        const result = await this.$axios.get('info/district', {
          params: { province_id: 1 },
        });
        this.districts = (result.data && result.data.data) || [];
      } catch (error) {
        console.log(error);
      }
    },
    async fetch() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get('v2/report-residential-customer-ar', {
          params: this.buildParams(),
        });
        this.summary = data.summary || {};
        this.items = (data.items && data.items.data) || [];
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const response = await this.$axios.get('v2/report-residential-customer-ar/export', {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `residential-customer-ar-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
